import { useEffect, useState } from 'react';
import {
  AlertToastType,
  ButtonFlavor,
  ButtonSize,
  ButtonType,
  DialogHeaderContent,
  DialogVariant,
  ExButton,
  ExDialog,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  SpinnerSize,
  ToastController,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import { ModelConfigDetails } from '../types/models.types';
import './ModelConfigDeleteConfirmationDialog.scss';

interface ModelConfigDeleteConfirmationDialogProps {
  open: boolean;
  configId: string;
  onClose: () => void;
  onDeleteSuccess: () => void;
  onDeleteBlocked: (modelConfig: ModelConfigDetails) => void;
}

function ModelConfigDeleteConfirmationDialog({
  open,
  configId,
  onClose,
  onDeleteSuccess,
  onDeleteBlocked,
}: ModelConfigDeleteConfirmationDialogProps) {
  const [loading, setLoading] = useState(true);
  const [modelConfig, setModelConfig] = useState<ModelConfigDetails | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [t] = useTranslation('common');

  useEffect(() => {
    let isMounted = true;
    const loadModelConfig = async () => {
      setLoading(true);
      try {
        const modelConfigData = await modelsApiService.getModelConfigDetails(configId);
        if (isMounted) {
          const fetchedModelConfig = modelConfigData?.data || null;
          if (fetchedModelConfig?.deployment_count && fetchedModelConfig.deployment_count > 0) {
            onDeleteBlocked(fetchedModelConfig);
          } else {
            setModelConfig(fetchedModelConfig);
          }
        }
      } catch (error) {
        if (isMounted) {
          const errorMessage = error instanceof Error ? error.message : t('models_error_delete_failed');
          ToastController.show({
            description: errorMessage,
            type: AlertToastType.ERROR,
          });
          onClose();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (open) {
      loadModelConfig();
    }

    return () => {
      isMounted = false;
    };
  }, [configId, open, onClose, onDeleteBlocked, t]);

  const handleCancel = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  const handleDelete = async () => {
    if (modelConfig?.deployment_count && modelConfig.deployment_count > 0) {
      onDeleteBlocked(modelConfig);
      return;
    }

    setLoading(true);
    setDeleteError(null);
    document.body.style.overflow = 'unset';
    let deleteSucceeded = false;

    try {
      const response = await modelsApiService.deleteModelConfiguration(configId);

      if (response.success) {
        ToastController.show({
          description: t('models_config_success_deleted'),
          type: AlertToastType.SUCCESS,
        });
        onDeleteSuccess();
        deleteSucceeded = true;
      } else {
        setDeleteError(response.message || response.error || t('models_error_delete_failed'));
      }
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : t('models_error_delete_failed'));
    } finally {
      setLoading(false);
      if (deleteSucceeded) onClose();
      document.body.style.overflow = 'unset';
    }
  };

  function getLoaderMessage() {
    return 'Deleting...';
  }

  return (
    <ExDialog
      {...(!loading && {
        dialogTitle: deleteError ? t('models_error_delete_failed') : t('models_delete_confirmation_title'),
      })}
      open={open}
      hideClose={loading}
      staticBackdrop
      variant={DialogVariant.SPACED}
      onCancel={handleCancel}
      headerContent={DialogHeaderContent.NEGATIVE}
    >
      {loading && (
        <div className="loader-dialog">
          <ExLoader
            variant={LoaderVariant.SPINNER}
            size={LoaderSize.MEDIUM}
            spinnerSize={SpinnerSize.MEDIUM}
            label={getLoaderMessage()}
            inline
          />
        </div>
      )}
      {!loading && (
        <div className="confirmation-container">
          <div>{deleteError || t('models_delete_confirmation_description')}</div>
        </div>
      )}
      {!loading && (
        <>
          <ExButton
            type={ButtonType.SECONDARY}
            data-testid="model-config-delete-confirmation-cancel-button"
            size={ButtonSize.DEFAULT}
            flavor={ButtonFlavor.BRANDED}
            onClick={handleCancel}
            slot="footer"
          >
            {t('close')}
          </ExButton>
          {!deleteError && (
            <ExButton
              type={ButtonType.PRIMARY}
              size={ButtonSize.DEFAULT}
              slot="footer"
              flavor={ButtonFlavor.RISKY}
              data-testid="model-config-delete-confirmation-button"
              onClick={handleDelete}
            >
              {t('delete_btn', 'Delete')}
            </ExButton>
          )}
        </>
      )}
    </ExDialog>
  );
}

export default ModelConfigDeleteConfirmationDialog;
